import { creditCheckTranslations } from '@/helpers/creditCheckTranslations';
import { useTranslation } from '@/hooks/useTranslation';
import { useGetAccountWithTokenQuery } from '@/services/accounts';
import { Box, FormikTextInput, Icon, TextInput } from '@palmetto/palmetto-components';
import { Field, useFormikContext } from 'formik';
import { useState } from 'react';
import { usePhoneInput } from 'react-international-phone';
import { useParams } from 'react-router-dom';

import { useInviteToken } from '../auth/useInviteToken';

import { getSsnRequirementsByProgramType } from './ApplicationForm';

/**
 * This component is used both within our application and via invite token
 * Any API queries made here or in child components need to use the invite token otherwise it won't work accessing outside of our app
 */
export function ApplicantInformation({
  isCreditApproved,
  isSubmitting,
  isLoading,
  prefix,
  isDobRequired = false,
}: {
  isCreditApproved?: boolean;
  isSubmitting: boolean;
  isLoading: boolean;
  prefix: 'primary' | 'secondary';
  isDobRequired?: boolean;
}) {
  const { initialValues, values, setFieldValue, setFieldTouched, errors, touched }: any = useFormikContext<any>();
  const { translate } = useTranslation({ useAccountPreference: true });
  const { id = '' } = useParams<{ id: string }>();
  const { inviteToken } = useInviteToken();
  const preferredLanguage = values?.language;
  const { data: account } = useGetAccountWithTokenQuery({ id, inviteToken });
  const [showSsn, setShowSsn] = useState(true);
  const ssnInformationByProgramType = getSsnRequirementsByProgramType(account?.programType);
  const label = ssnInformationByProgramType.label;
  const helpText = ssnInformationByProgramType.helpText;
  const placeHolder = ssnInformationByProgramType.placeHolder;
  const initialPhoneNumber = values?.[prefix]?.phoneNumber?.startsWith('+1')
    ? values[prefix].phoneNumber
    : `+1 ${values?.[prefix]?.phoneNumber ?? ''}`;
  const { inputValue: phoneNumber, handlePhoneValueChange } = usePhoneInput({
    value: initialPhoneNumber,
    onChange: (data) => {
      if (values) {
        setFieldValue(`${prefix}.phoneNumber`, data.phone);
      }
    },
  });
  const prefixIsPrimary = prefix === 'primary';

  const handleSsnChange = (data: React.ChangeEvent<HTMLInputElement>) => {
    if (data.target.value) {
      setFieldValue(`${prefix}.ssn`, data.target.value);
    }
  };

  return (
    <Box childGap={{ base: 'lg', desktop: 'xl' }}>
      <Box
        direction={{
          base: 'column',
          tablet: 'row',
        }}
        childGap={{ base: 'lg', desktop: 'xl' }}
      >
        <Field
          type="text"
          label={translate('firstName', creditCheckTranslations, preferredLanguage)}
          name={`${prefix}.firstName`}
          id={`${prefix}.firstName`}
          component={FormikTextInput}
          isRequired
          autoComplete="off"
          isDisabled={
            isSubmitting || isLoading || (initialValues?.[prefix]?.firstName && isCreditApproved && prefixIsPrimary)
          }
        />
        <Field
          type="text"
          label={translate('middleName', creditCheckTranslations, preferredLanguage)}
          name={`${prefix}.middleName`}
          id={`${prefix}.middleName`}
          component={FormikTextInput}
          autoComplete="off"
          isDisabled={
            isSubmitting ||
            isLoading ||
            ((initialValues?.[prefix]?.middleName ||
              (initialValues?.[prefix]?.firstName && initialValues?.[prefix]?.lastName)) &&
              isCreditApproved &&
              prefixIsPrimary)
          }
        />
        <Field
          type="text"
          label={translate('lastName', creditCheckTranslations, preferredLanguage)}
          name={`${prefix}.lastName`}
          id={`${prefix}.lastName`}
          component={FormikTextInput}
          isRequired
          autoComplete="off"
          isDisabled={
            isSubmitting || isLoading || (initialValues?.[prefix]?.lastName && isCreditApproved && prefixIsPrimary)
          }
        />
      </Box>
      <Box
        direction={{
          base: 'column',
          tablet: 'row',
        }}
        childGap={{ base: 'lg', desktop: 'xl' }}
      >
        <TextInput
          label={translate('phoneNumber', creditCheckTranslations, preferredLanguage)}
          name={`${prefix}.phoneNumber`}
          id={`${prefix}.phoneNumber`}
          isRequired
          autoComplete="off"
          onChange={handlePhoneValueChange}
          value={phoneNumber}
          isDisabled={
            isSubmitting ||
            isLoading ||
            (initialValues?.[prefix]?.phoneNumber && isCreditApproved && prefixIsPrimary)
          }
          placeholder="+1 (555) 555-5555"
          error={touched?.[prefix]?.phoneNumber && errors?.[prefix]?.phoneNumber}
        />
        <Field
          type="email"
          label={translate('email', creditCheckTranslations, preferredLanguage)}
          name={`${prefix}.email`}
          id={`${prefix}.email`}
          component={FormikTextInput}
          placeholder="name@example.com"
          isRequired
          autoComplete="off"
          isDisabled={
            isSubmitting || isLoading || (initialValues?.[prefix]?.email && isCreditApproved && prefixIsPrimary)
          }
        />
      </Box>
      <Box
        direction={{
          base: 'column',
          tablet: 'row',
        }}
        childGap={{ base: 'lg', desktop: 'xl' }}
      >
        <Box width="100%" style={{ marginBottom: '0' }}>
          <Field
            name={`${prefix}.dob`}
            id={`${prefix}.dob`}
            label={translate('dateOfBirth', creditCheckTranslations, preferredLanguage)}
            component={FormikTextInput}
            isDisabled={isSubmitting || isLoading}
            placeholder="MM-DD-YYYY"
            inputMask="date"
            isRequired={isDobRequired}
          />
        </Box>
        <Box width="100%" />
      </Box>
      <Box direction="row" childGap="md">
        <TextInput
          type={showSsn ? 'text' : 'password'}
          label={translate(label, creditCheckTranslations, preferredLanguage)}
          helpText={translate(helpText, creditCheckTranslations, preferredLanguage)}
          name={`${prefix}.ssn`}
          id={`${prefix}.ssn`}
          autoComplete="off"
          isRequired
          isDisabled={isSubmitting || isLoading}
          onBlur={() => {
            setShowSsn(false);
            setFieldTouched(`${prefix}.ssn`, true);
          }}
          inputMask={{
            numericOnly: true,
          }}
          placeholder={translate(placeHolder, creditCheckTranslations, preferredLanguage)}
          suffix={
            showSsn ? (
              <Icon size="sm" name="eye" onClick={() => setShowSsn(false)} style={{ cursor: 'pointer' }} />
            ) : (
              <Icon size="sm" name="eye-slash" onClick={() => setShowSsn(true)} style={{ cursor: 'pointer' }} />
            )
          }
          onChange={handleSsnChange}
          value={values?.[prefix]?.ssn}
          error={touched?.[prefix]?.ssn && errors?.[prefix]?.ssn}
        />
        <Box width="100%" />
      </Box>
    </Box>
  );
}
